import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

export interface ReadinessSummary {
  severity?: string;
  reasons?: string[];
  worst_property_id?: string | null;
  last_success_at?: string | null;
}

export interface ReadinessEntry {
  severity?: string;
  reasons?: string[];
  latest_snapshot_finished_at?: string | null;
  latest_snapshot_run_id?: string | null;
}

interface DataReadinessBannerProps {
  summary: ReadinessSummary | null;
  perProperty: Record<string, ReadinessEntry> | null;
  asOf?: string | null;
  propertyLabels?: Record<string, string>;
}

// Severity ordering so a dismissed banner re-appears only when things get worse.
const SEVERITY_RANK: Record<string, number> = {
  ok: 0,
  unknown: 1,
  warn: 2,
  error: 3,
};

const DISMISS_PREFIX = 'data-readiness-dismissed:';

function formatTimestamp(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

// Doctrine tokens (see index.css): amber = needs-attention/actionable,
// --error* = genuine error. warn/unknown both use the calm amber attention
// treatment; only a hard error uses the error palette.
function severityStyles(severity?: string | null): string {
  if (severity === 'error') {
    return 'border-[var(--error-border)] bg-[var(--error-bg)] text-[var(--error)]';
  }
  // warn + unknown + any other non-ok severity: amber attention.
  return 'border-[var(--accent)]/30 bg-[var(--accent-dim)] text-[var(--accent)]';
}

function headlineFor(severity?: string | null): string {
  switch (severity) {
    case 'error':
      return 'Pricing data unavailable';
    case 'warn':
      return 'Data may be stale';
    case 'unknown':
    default:
      return "Can't verify data freshness right now";
  }
}

// Turn the backend reason codes into one calm, specific clause. Returns null
// when there is nothing more specific to add.
function reasonClause(reasons: string[]): string | null {
  if (reasons.includes('pg_unavailable')) return 'The live database is unreachable.';
  if (reasons.includes('bq_blocked')) return 'The data warehouse is temporarily blocked.';
  if (reasons.includes('missing_snapshot')) return 'The latest data snapshot has not landed yet.';
  if (reasons.includes('stale_facts')) return 'The most recent facts are older than expected.';
  return null;
}

function buildDetail(
  severity: string | undefined,
  reasons: string[],
  asOfLabel: string | null,
): string {
  const parts: string[] = [];

  const specific = reasonClause(reasons);
  if (specific) parts.push(specific);

  if (asOfLabel) {
    parts.push(`Showing last verified data from ${asOfLabel}.`);
  } else {
    parts.push('Last verified time is not available yet.');
  }

  // Only claim a pricing pause for a genuine error; never as the default.
  if (severity === 'error') {
    parts.push('Pricing decisions may be paused.');
  }

  return parts.join(' ');
}

export function DataReadinessBanner({
  summary,
  perProperty,
  asOf,
  propertyLabels = {},
}: DataReadinessBannerProps) {
  const [expanded, setExpanded] = useState(false);
  const [dismissedRank, setDismissedRank] = useState<number | null>(null);

  const severity = summary?.severity;
  const worstProperty = summary?.worst_property_id ?? '__all__';
  const dismissKey = `${DISMISS_PREFIX}${worstProperty}`;
  const currentRank = severity ? (SEVERITY_RANK[severity] ?? 1) : 0;

  // Hydrate the dismissed rank for this property from session storage so the
  // banner stays hidden for the session unless severity worsens.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage.getItem(dismissKey);
      setDismissedRank(raw === null ? null : Number.parseInt(raw, 10));
    } catch {
      setDismissedRank(null);
    }
  }, [dismissKey]);

  const affectedProperties = useMemo(() => {
    if (!perProperty) return [];
    return Object.entries(perProperty)
      .filter(([, value]) => value?.severity && value.severity !== 'ok')
      .map(([key]) => propertyLabels[key] || key);
  }, [perProperty, propertyLabels]);

  if (!severity || severity === 'ok') return null;

  // Stay dismissed unless the situation has gotten worse than when dismissed.
  if (dismissedRank !== null && currentRank <= dismissedRank) return null;

  const asOfLabel = formatTimestamp(asOf ?? summary?.last_success_at);
  const reasons = summary?.reasons ?? [];
  const headline = headlineFor(severity);
  const detailCopy = buildDetail(severity, reasons, asOfLabel);
  const propertiesLabel =
    affectedProperties.length > 0 ? affectedProperties.join(', ') : 'all properties';

  const handleDismiss = () => {
    setDismissedRank(currentRank);
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(dismissKey, String(currentRank));
      } catch {
        // Best-effort only.
      }
    }
  };

  return (
    <div className={`border-b px-4 py-3 ${severityStyles(severity)}`}>
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-sm font-semibold">{headline}</div>
          <div className="text-xs text-[var(--text-secondary)]">{detailCopy}</div>
          {affectedProperties.length > 0 && (
            <div className="mt-0.5 text-xs text-[var(--text-secondary)]">
              Affected: {propertiesLabel}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 self-start">
          <button
            type="button"
            className="rounded px-2 py-1 text-xs uppercase tracking-wide text-[var(--text-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
          >
            {expanded ? 'Hide details' : 'View details'}
          </button>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            onClick={handleDismiss}
            aria-label="Dismiss data readiness notice"
            title="Dismiss for now"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {expanded && perProperty && (
        <div className="mt-3 grid gap-2 text-xs text-[var(--text-secondary)]">
          {Object.entries(perProperty).map(([propertyId, entry]) => (
            <div
              key={propertyId}
              className="flex flex-col gap-1 rounded border border-[var(--border-color)] p-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-[var(--text-primary)]">
                  {propertyLabels[propertyId] || propertyId}
                </span>
                <span className="uppercase">{entry.severity || 'not reported'}</span>
              </div>
              <div>Reasons: {(entry.reasons || []).join(', ') || 'none'}</div>
              <div>Facts run: {entry.latest_snapshot_run_id || 'not available'}</div>
              <div>As of: {formatTimestamp(entry.latest_snapshot_finished_at) ?? 'not available'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
